import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
  inject,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { SchoolByGroup, SearchSchoolResponse } from 'login-data-access';
import { Subscription } from 'rxjs';
import { filterSchools, groupSchools } from './select-school.utils';

export type SelectSchoolResult = SearchSchoolResponse;

@Component({
  selector: 'gep-select-school',
  imports: [ReactiveFormsModule],
  templateUrl: './select-school.component.html',
  styleUrl: './select-school.component.css',
})
export class SelectSchoolComponent implements AfterViewInit, OnDestroy {
  private readonly activeModal = inject(NgbActiveModal);
  private readonly cdr = inject(ChangeDetectorRef);

  @ViewChild('tableFilterInput')
  tableFilterInput?: ElementRef<HTMLInputElement>;

  schools: SearchSchoolResponse[] = [];
  isMobile = false;

  readonly filter = new FormControl('', { nonNullable: true });
  filteredSchools: SchoolByGroup[] = [];
  selectedSchool?: SearchSchoolResponse;

  private filterSubscription?: Subscription;

  ngAfterViewInit(): void {
    this.applySchoolFilter('');
    this.filterSubscription = this.filter.valueChanges.subscribe((value) => {
      this.applySchoolFilter(value);
    });

    if (this.isMobile) {
      this.focusFirstTableRow();
    }
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
  }

  configure(schools: SearchSchoolResponse[], isMobile: boolean): void {
    this.schools = schools;
    this.isMobile = isMobile;
    this.applySchoolFilter(this.filter.value);
    this.cdr.detectChanges();
    if (this.isMobile) {
      this.focusFirstTableRow();
    }
  }

  clickTableSchool(schoolSelected: SearchSchoolResponse): void {
    this.selectedSchool = schoolSelected;
  }

  confirm(): void {
    if (!this.selectedSchool) {
      return;
    }

    this.activeModal.close(this.selectedSchool);
  }

  handleKeyDown(event: KeyboardEvent): void {
    const rows = Array.from(document.querySelectorAll('.select-school-row'));
    const activeElement = document.activeElement;

    if (
      activeElement?.id === 'table-filtering-search' &&
      event.key === 'ArrowDown'
    ) {
      event.preventDefault();
      if (rows.length > 0) {
        (rows[0] as HTMLElement).focus();
      }
      return;
    }

    if (activeElement?.closest('tr') && activeElement.closest('tbody')) {
      const currentRow = activeElement.closest('tr');
      const currentIndex = rows.indexOf(currentRow!);

      if (event.key === 'ArrowDown' && currentIndex < rows.length - 1) {
        event.preventDefault();
        (rows[currentIndex + 1] as HTMLElement).focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        if (currentIndex > 0) {
          (rows[currentIndex - 1] as HTMLElement).focus();
        } else {
          this.tableFilterInput?.nativeElement?.focus();
        }
      } else if (event.key === 'Enter') {
        const selected = this.filteredSchools
          .flatMap((group) => group.ESCOLAS)
          .find((escola) => escola.VALUE === this.selectedSchool?.VALUE);
        if (selected) {
          this.clickTableSchool(selected);
        }
        this.confirm();
      }
    }
  }

  private applySchoolFilter(filterBy: string): void {
    if (!filterBy) {
      this.filteredSchools = groupSchools(this.schools);
      return;
    }

    this.filteredSchools = groupSchools(filterSchools(this.schools, filterBy));
  }

  private focusFirstTableRow(): void {
    setTimeout(() => {
      const rows = document.querySelectorAll('.select-school-row');
      if (rows.length === 0) {
        return;
      }

      const firstSchool = this.filteredSchools.flatMap((group) => group.ESCOLAS)[0];
      if (firstSchool) {
        this.clickTableSchool(firstSchool);
      }
      (rows[0] as HTMLElement).focus();
    }, 0);
  }
}
